import { supabase } from './supabase'

export type EmailTemplate =
  | 'welcome'
  | 'password-reset'
  | 'trade-alert'
  | 'trade-pool-notification'
  | 'subscription-confirmation'
  | 'invoice'
  | 'weekly-summary'
  | 'incomplete-trades-reminder'
  | 'no-trades-reminder'

interface EmailData {
  to: string
  template: EmailTemplate
  data?: Record<string, any>
  subject?: string
}

/**
 * Send an email using the Supabase Edge Function (AWS SES)
 * @param emailData - Email configuration
 * @returns Promise with result or error
 */
export async function sendEmail(emailData: EmailData) {
  try {
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: emailData
    })

    if (error) {
      console.error('Error sending email:', error)
      throw error
    }

    console.log('Email sent successfully:', data)
    return { success: true, data }
  } catch (error) {
    console.error('Failed to send email:', error)
    return { success: false, error }
  }
}

/**
 * Send welcome email to new users
 */
export async function sendWelcomeEmail(userEmail: string, userName?: string) {
  return sendEmail({
    to: userEmail,
    template: 'welcome',
    data: {
      name: userName,
      appUrl: window.location.origin
    }
  })
}

/**
 * Send password reset email
 */
export async function sendPasswordResetEmail(userEmail: string, resetUrl: string, userName?: string) {
  return sendEmail({
    to: userEmail,
    template: 'password-reset',
    data: {
      name: userName,
      resetUrl
    }
  })
}

/**
 * Send trade alert email
 */
export async function sendTradeAlertEmail(
  userEmail: string,
  tradeData: {
    symbol: string
    tradeType: string
    tradeAction?: string
    pnl?: string
    entryPrice?: string
    exitPrice?: string
    date?: string
    message?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'trade-alert',
    data: {
      name: userName,
      appUrl: window.location.origin,
      ...tradeData
    }
  })
}

/**
 * Send subscription confirmation email
 */
export async function sendSubscriptionConfirmationEmail(
  userEmail: string,
  subscriptionData: {
    plan?: string
    nextBillingDate?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'subscription-confirmation',
    data: {
      name: userName,
      appUrl: window.location.origin,
      ...subscriptionData
    }
  })
}

/**
 * Send trade pool notification email
 */
export async function sendTradePoolNotificationEmail(
  userEmail: string,
  tradeData: {
    poolName: string
    traderName: string
    symbol: string
    tradeType: string
    market?: string
    entryPrice?: string
    exitPrice?: string
    pnl?: string
    date?: string
    tradeUrl?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'trade-pool-notification',
    data: {
      name: userName,
      appUrl: window.location.origin,
      ...tradeData
    }
  })
}

/**
 * Send invoice email
 */
export async function sendInvoiceEmail(
  userEmail: string,
  invoiceData: {
    invoiceNumber: string
    date?: string
    plan?: string
    period?: string
    amount: string
    invoiceUrl?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'invoice',
    data: {
      name: userName,
      ...invoiceData
    }
  })
}

/** Trade row for weekly summary (best/worst) */
export interface WeeklySummaryTradeRow {
  symbol: string
  pnl: string | number
  date?: string
  tradeUrl?: string
}

/**
 * Send weekly summary email
 */
export async function sendWeeklySummaryEmail(
  userEmail: string,
  data: {
    periodLabel?: string
    totalTrades: number
    winRatePct?: number
    winRate?: number
    bestTrades?: WeeklySummaryTradeRow[]
    worstTrades?: WeeklySummaryTradeRow[]
    appUrl?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'weekly-summary',
    data: {
      name: userName,
      appUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
      ...data
    }
  })
}

/** Trade row for incomplete-trades reminder (missing strategy, notes, stop loss, take profit) */
export interface IncompleteTradeRow {
  id?: string
  symbol: string
  date?: string
  openedAt?: string
  tradeUrl?: string
}

/**
 * Send incomplete trades reminder email (lists trades missing strategy, notes, stop loss, take profit)
 */
export async function sendIncompleteTradesReminderEmail(
  userEmail: string,
  data: {
    missingStrategy?: IncompleteTradeRow[]
    missingNotes?: IncompleteTradeRow[]
    missingStopLoss?: IncompleteTradeRow[]
    missingTakeProfit?: IncompleteTradeRow[]
    appUrl?: string
  },
  userName?: string
) {
  return sendEmail({
    to: userEmail,
    template: 'incomplete-trades-reminder',
    data: {
      name: userName,
      appUrl: typeof window !== 'undefined' ? window.location.origin : undefined,
      ...data
    }
  })
}

/**
 * Send no-trades reminder email (for users who have not added any trade yet)
 */
export async function sendNoTradesReminderEmail(
  userEmail: string,
  options?: { appUrl?: string; userName?: string }
) {
  return sendEmail({
    to: userEmail,
    template: 'no-trades-reminder',
    data: {
      name: options?.userName,
      appUrl: options?.appUrl ?? (typeof window !== 'undefined' ? window.location.origin : undefined)
    }
  })
}
